import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Stack,
  Text,
  Pivot,
  PivotItem,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
} from "@fluentui/react";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import AddButton from "@/ui-controls/AddButton";
import SearchBox from "@/ui-controls/SearchBox";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import ShimmerList from "@/ui-controls/ShimmerList";
import RigthPanel, { RigthPanelType } from "@/ui-controls/RigthPanel";
import NewConfigurationForm from "@/components/integrations/configurations/NewConfigurationForm";
import EditConfigurationForm from "@/components/integrations/configurations/EditConfigurationForm";
import NewCustomerSettingForm from "@/components/integrations/configurations/NewCustomerSettingForm";
import EditCustomerSettingForm from "@/components/integrations/configurations/EditCustomerSettingForm";
import ConfigurationFilterPanel, {
  ConfigurationFilterOptions,
  defaultConfigFilters,
} from "@/components/integrations/configurations/ConfigurationFilterPanel";
import {
  IntegrationDto,
  IntegrationConfigurationDto,
  CustomerIntegrationSettingDto,
  listIntegrations,
  listIntegrationConfigurations,
  listCustomerIntegrationSettings,
  setupCustomerIntegrationSetting,
  deleteCustomerIntegrationSetting,
  updateIntegrationConfiguration,
  deleteIntegrationConfiguration,
} from "@/api/integrations";
import { Customer, getAllActiveCustomers } from "@/api/customers";

const scopeLabels: Record<number, string> = {
  1: "Global",
  2: "Customer",
  3: "User",
};

const authLabels: Record<number, string> = {
  1: "API Key",
  2: "Username / Password",
  3: "Username / Password / Company Code",
};

const IntegrationConfigurationsPage = () => {
  const [integrations, setIntegrations] = useState<IntegrationDto[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [configs, setConfigs] = useState<IntegrationConfigurationDto[]>([]);
  const [settings, setSettings] = useState<CustomerIntegrationSettingDto[]>(
    []
  );
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Search & Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] =
    useState<ConfigurationFilterOptions>({ ...defaultConfigFilters });
  const [appliedFilters, setAppliedFilters] =
    useState<ConfigurationFilterOptions>({ ...defaultConfigFilters });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // Config panels
  const [isNewConfigOpen, setIsNewConfigOpen] = useState(false);
  const [isEditConfigOpen, setIsEditConfigOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] =
    useState<IntegrationConfigurationDto | null>(null);
  const [configToDelete, setConfigToDelete] =
    useState<IntegrationConfigurationDto | null>(null);

  // Setting panels
  const [isNewSettingOpen, setIsNewSettingOpen] = useState(false);
  const [isEditSettingOpen, setIsEditSettingOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] =
    useState<CustomerIntegrationSettingDto | null>(null);
  const [settingToDelete, setSettingToDelete] =
    useState<CustomerIntegrationSettingDto | null>(null);

  const loadData = useCallback(() => {
    setLoadingConfigs(true);
    setLoadingSettings(true);

    Promise.all([
      listIntegrations(undefined, 1000),
      getAllActiveCustomers(),
      listIntegrationConfigurations(undefined, undefined, undefined, undefined, 1000),
      listCustomerIntegrationSettings(undefined, undefined, undefined, 1000),
    ])
      .then(([intData, custData, cfgData, setData]) => {
        setIntegrations(intData?.result || []);
        setCustomers(custData || []);
        setConfigs(cfgData?.result || []);
        setSettings(setData?.result || []);
      })
      .catch(() => {
        setIntegrations([]);
        setCustomers([]);
        setConfigs([]);
        setSettings([]);
      })
      .finally(() => {
        setLoadingConfigs(false);
        setLoadingSettings(false);
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getIntegrationName = (integrationId: number) => {
    const integration = integrations.find((i) => i.id === integrationId);
    return integration ? `${integration.code} - ${integration.name}` : integrationId.toString();
  };

  const getCustomerName = (customerId: number | null) => {
    if (customerId == null) return "-";
    const customer = customers.find((c) => c.Id === customerId);
    return customer
      ? `${customer.Name} (${customerId})`
      : customerId.toString();
  };

  // Filtered configurations
  const filteredConfigs = useMemo(() => {
    let items = configs;

    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      items = items.filter((c) =>
        (c.configName || "").toLowerCase().includes(lower)
      );
    }

    if (appliedFilters.statusFilter === "active") {
      items = items.filter((c) => c.isActive);
    } else if (appliedFilters.statusFilter === "inactive") {
      items = items.filter((c) => !c.isActive);
    }

    if (appliedFilters.integrationId !== undefined) {
      items = items.filter(
        (c) => c.integrationId === appliedFilters.integrationId
      );
    }

    if (appliedFilters.customerId !== undefined) {
      items = items.filter((c) => c.customerId === appliedFilters.customerId);
    }

    if (appliedFilters.scopeType !== undefined) {
      items = items.filter((c) => c.scopeType === appliedFilters.scopeType);
    }

    return items;
  }, [configs, searchTerm, appliedFilters]);

  // Filtered settings
  const filteredSettings = useMemo(() => {
    let items = settings;

    if (searchTerm.trim()) {
      // For settings, search by customer name
      const lower = searchTerm.toLowerCase();
      items = items.filter((s) => {
        const name = getCustomerName(s.customerId).toLowerCase();
        return name.includes(lower);
      });
    }

    if (appliedFilters.statusFilter === "active") {
      items = items.filter((s) => s.isActive);
    } else if (appliedFilters.statusFilter === "inactive") {
      items = items.filter((s) => !s.isActive);
    }

    if (appliedFilters.integrationId !== undefined) {
      items = items.filter(
        (s) => s.integrationId === appliedFilters.integrationId
      );
    }

    if (appliedFilters.customerId !== undefined) {
      items = items.filter((s) => s.customerId === appliedFilters.customerId);
    }

    return items;
  }, [settings, searchTerm, appliedFilters, customers]);

  const hasFiltersApplied =
    appliedFilters.statusFilter !== "all" ||
    appliedFilters.integrationId !== undefined ||
    appliedFilters.customerId !== undefined ||
    appliedFilters.scopeType !== undefined;

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
  };

  const handleRefresh = () => {
    setSearchTerm("");
    setSelectedFilters({ ...defaultConfigFilters });
    setAppliedFilters({ ...defaultConfigFilters });
    loadData();
  };

  const handleFiltersChange = (filters: ConfigurationFilterOptions) => {
    setSelectedFilters(filters);
  };

  const handleApplyFilters = (filters: ConfigurationFilterOptions) => {
    setAppliedFilters(filters);
  };

  const handleDeactivateConfig = async (config: IntegrationConfigurationDto) => {
    try {
      await updateIntegrationConfiguration({
        id: config.id,
        integrationId: config.integrationId,
        scopeType: config.scopeType,
        customerId: config.customerId,
        userId: config.userId,
        authType: config.authType,
        apiKey: config.apiKey,
        username: config.username,
        password: config.password,
        companyCode: config.companyCode,
        configJson: config.configJson,
        configName: config.configName,
        effectiveFrom: config.effectiveFrom,
        effectiveTo: config.effectiveTo,
        isActive: false,
        isTestMode: config.isTestMode,
      });
      loadData();
    } catch (error) {
      console.error("Error deactivating configuration:", error);
    }
  };

  const handleReactivateConfig = async (config: IntegrationConfigurationDto) => {
    try {
      await updateIntegrationConfiguration({
        id: config.id,
        integrationId: config.integrationId,
        scopeType: config.scopeType,
        customerId: config.customerId,
        userId: config.userId,
        authType: config.authType,
        apiKey: config.apiKey,
        username: config.username,
        password: config.password,
        companyCode: config.companyCode,
        configJson: config.configJson,
        configName: config.configName,
        effectiveFrom: config.effectiveFrom,
        effectiveTo: config.effectiveTo,
        isActive: true,
        isTestMode: config.isTestMode,
      });
      loadData();
    } catch (error) {
      console.error("Error reactivating configuration:", error);
    }
  };

  const handleDeleteConfig = async () => {
    if (!configToDelete) return;
    try {
      await deleteIntegrationConfiguration(configToDelete.id);
      setConfigToDelete(null);
      loadData();
    } catch (error) {
      console.error("Error deleting configuration:", error);
      setConfigToDelete(null);
    }
  };

  const handleDeactivateSetting = async (setting: CustomerIntegrationSettingDto) => {
    try {
      await setupCustomerIntegrationSetting({
        integrationId: setting.integrationId,
        customerId: setting.customerId,
        usesMasterConfig: setting.usesMasterConfig,
        isActive: false,
      });
      loadData();
    } catch (error) {
      console.error("Error deactivating setting:", error);
    }
  };

  const handleReactivateSetting = async (setting: CustomerIntegrationSettingDto) => {
    try {
      await setupCustomerIntegrationSetting({
        integrationId: setting.integrationId,
        customerId: setting.customerId,
        usesMasterConfig: setting.usesMasterConfig,
        isActive: true,
      });
      loadData();
    } catch (error) {
      console.error("Error reactivating setting:", error);
    }
  };

  const handleDeleteSetting = async () => {
    if (!settingToDelete) return;
    try {
      await deleteCustomerIntegrationSetting(settingToDelete.id);
      setSettingToDelete(null);
      loadData();
    } catch (error) {
      console.error("Error deleting setting:", error);
      setSettingToDelete(null);
    }
  };

  const handleClearFilters = () => {
    setSelectedFilters({ ...defaultConfigFilters });
    setAppliedFilters({ ...defaultConfigFilters });
  };

  const configColumns: DataGridColumn[] = [
    {
      key: "integration",
      name: "Integration",
      minWidth: 140,
      maxWidth: 220,
      onRender: (item: IntegrationConfigurationDto) =>
        getIntegrationName(item.integrationId),
    },
    {
      key: "configName",
      name: "Config Name",
      fieldName: "configName",
      minWidth: 200,
      maxWidth: 350,
      onRender: (item: IntegrationConfigurationDto) =>
        item.configName || "-",
    },
    {
      key: "scopeType",
      name: "Scope",
      minWidth: 80,
      maxWidth: 100,
      onRender: (item: IntegrationConfigurationDto) =>
        scopeLabels[item.scopeType] || item.scopeType.toString(),
    },
    {
      key: "authType",
      name: "Auth",
      minWidth: 220,
      maxWidth: 320,
      onRender: (item: IntegrationConfigurationDto) =>
        authLabels[item.authType] || item.authType.toString(),
    },
    {
      key: "customerId",
      name: "Customer",
      minWidth: 120,
      maxWidth: 200,
      onRender: (item: IntegrationConfigurationDto) =>
        getCustomerName(item.customerId),
    },
    {
      key: "isActive",
      name: "Status",
      minWidth: 80,
      maxWidth: 100,
      onRender: (item: IntegrationConfigurationDto) => (
        <Text
          styles={{
            root: {
              color: item.isActive ? "#107c10" : "#a80000",
              fontWeight: 600,
            },
          }}
        >
          {item.isActive ? "Active" : "Inactive"}
        </Text>
      ),
    },
    {
      key: "actions",
      name: "Actions",
      minWidth: 80,
      maxWidth: 80,
      onRender: (item: IntegrationConfigurationDto) => (
        <MoreActionsButton
          menu={[
            {
              key: "edit",
              text: "Edit",
              iconProps: { iconName: "Edit" },
              onClick: () => {
                setSelectedConfig(item);
                setIsEditConfigOpen(true);
              },
            },
            ...(item.isActive
              ? [
                  {
                    key: "deactivate",
                    text: "Deactivate",
                    iconProps: { iconName: "CircleStop" },
                    onClick: () => handleDeactivateConfig(item),
                  },
                ]
              : [
                  {
                    key: "reactivate",
                    text: "Reactivate",
                    iconProps: { iconName: "PlayResume" },
                    onClick: () => handleReactivateConfig(item),
                  },
                  {
                    key: "delete",
                    text: "Delete Permanently",
                    iconProps: { iconName: "Delete" },
                    onClick: () => setConfigToDelete(item),
                  },
                ]),
          ]}
        />
      ),
    },
  ];

  const settingColumns: DataGridColumn[] = [
    {
      key: "integration",
      name: "Integration",
      minWidth: 140,
      maxWidth: 220,
      onRender: (item: CustomerIntegrationSettingDto) =>
        getIntegrationName(item.integrationId),
    },
    {
      key: "customerId",
      name: "Customer",
      minWidth: 120,
      maxWidth: 200,
      onRender: (item: CustomerIntegrationSettingDto) =>
        getCustomerName(item.customerId),
    },
    {
      key: "usesMasterConfig",
      name: "Uses Master",
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: CustomerIntegrationSettingDto) =>
        item.usesMasterConfig ? "Yes" : "No",
    },
    {
      key: "isActive",
      name: "Status",
      minWidth: 80,
      maxWidth: 100,
      onRender: (item: CustomerIntegrationSettingDto) => (
        <Text
          styles={{
            root: {
              color: item.isActive ? "#107c10" : "#a80000",
              fontWeight: 600,
            },
          }}
        >
          {item.isActive ? "Active" : "Inactive"}
        </Text>
      ),
    },
    {
      key: "actions",
      name: "Actions",
      minWidth: 80,
      maxWidth: 80,
      onRender: (item: CustomerIntegrationSettingDto) => (
        <MoreActionsButton
          menu={[
            ...(item.isActive
              ? [
                  {
                    key: "edit",
                    text: "Edit",
                    iconProps: { iconName: "Edit" },
                    onClick: () => {
                      setSelectedSetting(item);
                      setIsEditSettingOpen(true);
                    },
                  },
                  {
                    key: "deactivate",
                    text: "Deactivate",
                    iconProps: { iconName: "Cancel" },
                    onClick: () => handleDeactivateSetting(item),
                  },
                ]
              : [
                  {
                    key: "reactivate",
                    text: "Reactivate",
                    iconProps: { iconName: "Accept" },
                    onClick: () => handleReactivateSetting(item),
                  },
                  {
                    key: "delete",
                    text: "Delete permanently",
                    iconProps: { iconName: "Delete" },
                    onClick: () => setSettingToDelete(item),
                  },
                ]),
          ]}
        />
      ),
    },
  ];

  const loading = loadingConfigs || loadingSettings;

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-left-section">
          <div>
            <h2 className="page-breadcrumb-title">
              Integration Configurations
            </h2>
            <div style={{ color: "#605e5c", fontSize: "14px" }}>
              Manage integration configurations and customer settings
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <AddButton
            text="New Config"
            onClick={() => setIsNewConfigOpen(true)}
          />
          <SearchBox
            placeholder="Search by config name..."
            onSearch={handleSearch}
            disabled={loading}
          />
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              iconProps={{ iconName: "Filter" }}
              title="Filter configurations"
              ariaLabel="Filter configurations"
              onClick={() => setIsFilterPanelOpen(true)}
              disabled={loading}
              styles={{
                root: {
                  width: 40,
                  height: 40,
                  backgroundColor: hasFiltersApplied
                    ? "#deecf9"
                    : "transparent",
                },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: {
                  fontSize: 18,
                  color: loading ? "#a19f9d" : "#0078d4",
                },
                rootPressed: { backgroundColor: "#e1dfdd" },
                rootDisabled: { backgroundColor: "transparent" },
              }}
            />
            {hasFiltersApplied && (
              <div
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  backgroundColor: "#d13438",
                  color: "white",
                  borderRadius: "50%",
                  width: "16px",
                  height: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "600",
                  zIndex: 1,
                }}
              >
                1
              </div>
            )}
          </div>
          <IconButton
            iconProps={{ iconName: "Refresh" }}
            title="Refresh"
            ariaLabel="Refresh"
            onClick={handleRefresh}
            disabled={loading}
            styles={{
              root: { width: 40, height: 40 },
              rootHovered: { backgroundColor: "#f3f2f1" },
              icon: {
                fontSize: 18,
                color: loading ? "#a19f9d" : "#0078d4",
              },
              rootPressed: { backgroundColor: "#e1dfdd" },
              rootDisabled: { backgroundColor: "transparent" },
            }}
          />
        </div>
      </div>

      <ConfigurationFilterPanel
        isOpen={isFilterPanelOpen}
        onDismiss={() => setIsFilterPanelOpen(false)}
        selectedFilters={selectedFilters}
        onFiltersChange={handleFiltersChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />

      <div style={{ padding: "16px" }}>
        <Pivot>
          <PivotItem headerText="Configurations">
            <Stack
              tokens={{ childrenGap: 12 }}
              styles={{ root: { marginTop: 12 } }}
            >
              {loadingConfigs ? (
                <ShimmerList numberOfColumns={7} />
              ) : (
                <DataGrid items={filteredConfigs} columns={configColumns} />
              )}
            </Stack>
          </PivotItem>
          <PivotItem headerText="Customer Settings">
            <Stack
              tokens={{ childrenGap: 12 }}
              styles={{ root: { marginTop: 12 } }}
            >
              <Text
                variant="medium"
                styles={{ root: { color: "#605e5c", lineHeight: "20px" } }}
              >
                Customer settings control whether a customer can fall back to
                the global (master) integration configuration. By default, all
                customers inherit the global config. To restrict a customer to
                only use their own customer-scoped configuration, add a setting
                with <strong>Uses Master Config</strong> turned off. If no
                customer-scoped config exists and master fallback is disabled,
                the integration will not resolve for that customer.
              </Text>
              <Stack horizontal horizontalAlign="end">
                <AddButton
                  text="New Setting"
                  onClick={() => setIsNewSettingOpen(true)}
                />
              </Stack>
              {loadingSettings ? (
                <ShimmerList numberOfColumns={7} />
              ) : (
                <DataGrid items={filteredSettings} columns={settingColumns} />
              )}
            </Stack>
          </PivotItem>
        </Pivot>
      </div>

      <RigthPanel
        header="New Configuration"
        isOpen={isNewConfigOpen}
        type={RigthPanelType.medium}
        dismissPanel={() => setIsNewConfigOpen(false)}
        childComponent={NewConfigurationForm}
        childProps={{
          integrations,
          onSave: () => {
            setIsNewConfigOpen(false);
            loadData();
          },
          onClose: () => setIsNewConfigOpen(false),
        }}
      />
      {selectedConfig && (
        <RigthPanel
          header="Edit Configuration"
          isOpen={isEditConfigOpen}
          type={RigthPanelType.medium}
          dismissPanel={() => {
            setIsEditConfigOpen(false);
            setSelectedConfig(null);
          }}
          childComponent={EditConfigurationForm}
          childProps={{
            integrationId: selectedConfig.integrationId,
            integrationCode:
              integrations.find(
                (i) => i.id === selectedConfig.integrationId
              )?.code ?? "",
            configuration: selectedConfig,
            onSave: () => {
              setIsEditConfigOpen(false);
              setSelectedConfig(null);
              loadData();
            },
            onClose: () => {
              setIsEditConfigOpen(false);
              setSelectedConfig(null);
            },
          }}
        />
      )}
      <RigthPanel
        header="New Customer Setting"
        isOpen={isNewSettingOpen}
        type={RigthPanelType.small}
        dismissPanel={() => setIsNewSettingOpen(false)}
        childComponent={NewCustomerSettingForm}
        childProps={{
          integrations,
          onSave: () => {
            setIsNewSettingOpen(false);
            loadData();
          },
          onClose: () => setIsNewSettingOpen(false),
        }}
      />
      {selectedSetting && (
        <RigthPanel
          header="Edit Customer Setting"
          isOpen={isEditSettingOpen}
          type={RigthPanelType.small}
          dismissPanel={() => {
            setIsEditSettingOpen(false);
            setSelectedSetting(null);
          }}
          childComponent={EditCustomerSettingForm}
          childProps={{
            integrationId: selectedSetting.integrationId,
            setting: selectedSetting,
            onSave: () => {
              setIsEditSettingOpen(false);
              setSelectedSetting(null);
              loadData();
            },
            onClose: () => {
              setIsEditSettingOpen(false);
              setSelectedSetting(null);
            },
          }}
        />
      )}

      <Dialog
        hidden={!settingToDelete}
        onDismiss={() => setSettingToDelete(null)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Delete Customer Setting Permanently",
          subText: `Are you sure you want to permanently delete the setting for "${getCustomerName(settingToDelete?.customerId ?? 0)}" on "${getIntegrationName(settingToDelete?.integrationId ?? 0)}"? This action cannot be undone.`,
        }}
        modalProps={{ isBlocking: false }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleDeleteSetting} text="Delete" />
          <DefaultButton onClick={() => setSettingToDelete(null)} text="Cancel" />
        </DialogFooter>
      </Dialog>

      <Dialog
        hidden={!configToDelete}
        onDismiss={() => setConfigToDelete(null)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Delete Configuration Permanently",
          subText: `Are you sure you want to permanently delete "${configToDelete?.configName || configToDelete?.id}"? This action cannot be undone.`,
        }}
        modalProps={{ isBlocking: false }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleDeleteConfig} text="Delete" />
          <DefaultButton onClick={() => setConfigToDelete(null)} text="Cancel" />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default IntegrationConfigurationsPage;
